// The shot zone chart: no page draws it (fix-all round, 10/2). Its dots
// are generated below from a seeded random generator, nudged by the
// athlete's scoring mix and efficiency, not read from real shot locations
// (no feed the app reads carries any), so every profile shows the player
// radar where it was, at every level. Kept, with ShotZone (v2/profileSections) and
// ShotZoneCard (v2/mobile/mobileProfileSections), for when shot data
// exists.

// Deterministic PRNG so the chart is stable per athlete.
const mulberry = (seed) => () => {
  let t = (seed += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const POTENCY_STOPS = ["#2E6FF2", "#22B5D3", "#4CAF50", "#F4C20D", "#F2711C", "#E53935"];
const lerp = (a, b, t) => Math.round(a + (b - a) * t);
const hex = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const potencyColor = (t) => {
  const x = Math.max(0, Math.min(1, t)) * (POTENCY_STOPS.length - 1);
  const i = Math.floor(x), f = x - i;
  const a = hex(POTENCY_STOPS[i]), b = hex(POTENCY_STOPS[Math.min(i + 1, POTENCY_STOPS.length - 1)]);
  return `rgb(${lerp(a[0], b[0], f)},${lerp(a[1], b[1], f)},${lerp(a[2], b[2], f)})`;
};

const HOOP = { x: 150, y: 46 };

// Density + heat biased by the athlete's real scoring mix + efficiency.
const buildDots = (profile) => {
  const rnd = mulberry(profile.seed || 1);
  const threeBias = (profile.threeShare || 30) / 100; // perimeter weighting
  const effBias = profile.efficiency != null ? Math.max(0, Math.min(1, profile.efficiency / 70)) : 0.5;
  const dots = [];
  for (let x = 28; x <= 272; x += 18) {
    for (let y = 40; y <= 250; y += 20) {
      if (rnd() < 0.12) continue;
      const d = Math.hypot(x - HOOP.x, y - HOOP.y);
      const near = Math.max(0, 1 - d / 240);
      const perimeter = Math.max(0, Math.min(1, (d - 90) / 150));
      // paint density when 2-heavy, perimeter density when 3-heavy
      const zoneWeight = near * (1 - threeBias) + perimeter * threeBias;
      // Wide random spread so the map shows the design's full blue→red range;
      // the athlete's real efficiency + zone mix shifts the center of mass.
      const potency = Math.max(0, Math.min(1, effBias * 0.25 + zoneWeight * 0.3 + rnd() * 0.75 - 0.15));
      // Strong size variance: impact zones grow large enough to OVERLAP the
      // 18px grid, so where the shooter is most dangerous reads as a cluster.
      const impact = zoneWeight * 0.6 + potency * 0.4;
      const jitter = rnd() * 2;
      const r = 1.5 + impact * 9 + jitter;
      // zone and jitter feed the frequency-only view; the random draws stay
      // in the same order, so r and color are unchanged.
      dots.push({ x, y, r, zone: zoneWeight, jitter, color: potencyColor(potency) });
    }
  }
  return dots;
};

// Legend dots in the neutral tertiary token: frequency is size, not colour.
const FreqLegend = () => (
  <div className="flex items-end gap-2" aria-hidden="true">
    {[2, 3.5, 5, 6.5, 8].map((r, i) => (
      <span key={i} className="inline-block rounded-full bg-bm-tertiary" style={{ width: r * 2, height: r * 2 }} />
    ))}
  </div>
);

// POTENCY_STOPS is a data scale (cold to hot), not brand colour.
const PotencyLegend = () => (
  <div>
    <div className="flex items-center gap-1.5" aria-hidden="true">
      {Array.from({ length: 18 }).map((_, i) => (
        <span key={i} className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: potencyColor(i / 17) }} />
      ))}
    </div>
    <div className="mt-1 flex justify-between pr-1">
      <span className="bm-body-s text-bm-tertiary">low</span>
      <span className="bm-body-s text-bm-tertiary">high</span>
    </div>
  </div>
);

// Every potency dot in the single-encoding view has this radius, so colour
// alone carries the read.
const POTENCY_R = 6;

// Frequency alone: the zone weight (the paint / perimeter mix from the
// athlete's scoring) stretched over this court's own range, so the busiest
// zones read largest, with a little of the chart's jitter kept.
const frequencyRadii = (dots) => {
  const zones = dots.map((d) => d.zone);
  const min = Math.min(...zones);
  const spread = Math.max(...zones) - min || 1;
  return dots.map((d) => 1.5 + ((d.zone - min) / spread) * 8 + d.jitter * 0.5);
};

const COURT_LABEL = {
  both: "Court map of shot frequency and potency",
  frequency: "Court map of shot frequency",
  potency: "Court map of shot potency",
};

// The court and its dots. `mode` picks the encoding: "both" (dot size is
// frequency, colour is potency), "frequency" (sized dots in the V2 orange)
// or "potency" (equal dots on the potency ramp).
const Court = ({ dots, mode, className }) => (
  // Court lines take the bm-strong token through currentColor.
  <svg viewBox="0 0 300 270" className={className} style={{ maxHeight: 320 }} role="img" aria-label={COURT_LABEL[mode] || COURT_LABEL.both}>
    <g fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="10" y="20" width="280" height="240" />
      <rect x="115" y="20" width="70" height="115" />
      <circle cx="150" cy="135" r="32" />
      <line x1="130" y1="32" x2="170" y2="32" />
      <circle cx={HOOP.x} cy={HOOP.y} r="7" />
      <path d="M132 46 A18 18 0 0 0 168 46" />
      <path d="M35 20 L35 70 A125 125 0 0 0 265 70 L265 20" />
      <path d="M110 260 A40 40 0 0 1 190 260" />
    </g>
    {mode === "frequency" ? (
      <g className="text-bm-orange" fill="currentColor">
        {frequencyRadii(dots).map((r, i) => (
          <circle key={i} cx={dots[i].x} cy={dots[i].y} r={r} opacity="0.75" />
        ))}
      </g>
    ) : (
      dots.map((d, i) => <circle key={i} cx={d.x} cy={d.y} r={mode === "potency" ? POTENCY_R : d.r} fill={d.color} opacity="0.92" />)
    )}
  </svg>
);

// The low-to-high scale under a bare chart on phones (Figma 2020:1939): the
// potency ramp as a 6px bar (fine steps of the dots' own colours), or the
// frequency dots growing in the orange.
const RAMP_STEPS = 24;
export const ShotZoneScale = ({ mode = "potency" }) => (
  <div className="flex items-center gap-2">
    <span className="bm-body-xs text-bm-muted">low</span>
    {mode === "frequency" ? (
      <span aria-hidden="true" className="flex min-w-0 flex-1 items-center justify-between text-bm-orange">
        {[2, 3, 4, 5, 6].map((r) => (
          <span key={r} className="inline-block shrink-0 rounded-full bg-current opacity-75" style={{ width: r * 2, height: r * 2 }} />
        ))}
      </span>
    ) : (
      <span aria-hidden="true" className="flex h-[6px] min-w-0 flex-1 overflow-hidden rounded-full">
        {Array.from({ length: RAMP_STEPS }).map((_, i) => (
          <span key={i} className="h-full flex-1" style={{ background: potencyColor(i / (RAMP_STEPS - 1)) }} />
        ))}
      </span>
    )}
    <span className="bm-body-xs text-bm-muted">high</span>
  </div>
);

// A sub-section of the Season box score card, so its heading matches the
// "Shooting efficiency" block beside it on the V2 profile (bm-h-s over a
// bm-body-s line) rather than the card's own SectionTitle.
//
// `mode` and `bare` are for the phone card (mobile/mobileProfileSections):
// `bare` draws the court alone, without this heading and the two legends,
// because the card carries its own head, the Frequency / Potency switch and
// the scale. The defaults render the desktop chart.
const ShotZoneChart = ({ profile, mode = "both", bare = false }) => {
  const dots = buildDots(profile || {});
  if (bare) return <Court dots={dots} mode={mode} className="w-full text-bm-strong" />;
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3">
        <h3 className="bm-h-s text-bm-ink">Shot zone chart</h3>
        <span className="bm-body-s text-bm-muted">Where the scoring comes from, and how efficiently, by court zone</span>
      </div>

      <p className="mb-2 mt-5 bm-overline">Shot frequency</p>
      <FreqLegend />
      <p className="mb-2 mt-4 bm-overline">Shot potency</p>
      <PotencyLegend />

      <Court dots={dots} mode={mode} className="mt-4 w-full text-bm-strong" />
    </div>
  );
};

export default ShotZoneChart;
